/**
 * Mobile-only ``position: fixed`` filler that occupies the bottom
 * ``env(safe-area-inset-bottom)`` strip of the viewport. Lets all
 * other bottom-anchored elements (BallotSheet, BallotBackdrop, chat
 * input via padding-bottom) end at the TOP of this filler instead
 * of at the actual screen edge — the filler then renders the right
 * color in the home-indicator / safe-area region.
 *
 * Color matches whatever sits immediately above the filler:
 *   - Teal when the user is on the new chat page with the sheet
 *     fully closed (only the teal grabber band is visible at the
 *     bottom) — continues the band into the safe area.
 *   - Blue1 otherwise — matches the chat scroll container or the
 *     SampleBallotView's surface when the sheet is open.
 *
 * ``pointer-events: none`` so swipes through this strip still reach
 * the page. Zero-height on devices without a safe-area inset.
 */
import React from 'react';

import { Box } from '@mui/material';

import { useBallotSheet } from '@/providers/BallotSheetProvider';
import { useCanvasBackground } from '@/hooks/useCanvasBackground';
import { primitiveColors, semanticColors } from '@/theme/tokens';

const SafeAreaFiller: React.FC = () => {
  const { chatInputRect, detent, isDragging, closedDetentFrac } = useBallotSheet();
  // Teal whenever the element directly above the filler is teal:
  //   - New chat landing with the sheet fully closed (only the teal
  //     grabber band visible at the bottom).
  //   - MobileAddressBar mounted (gated on ``closedDetentFrac > 0``):
  //     the bar's teal background sits directly above the safe-area
  //     strip in both the new- and active-chat flows.
  // Blue1 otherwise — matches the chat scroll container / sheet body
  // when the sheet is open or no bar is present.
  const isMobileAddressBarActive = closedDetentFrac > 0;
  const isAtClosedRestOnLanding = chatInputRect === null && detent === 'closed' && !isDragging;
  const isTealBand = isMobileAddressBarActive || isAtClosedRestOnLanding;
  const fillColor = isTealBand ? semanticColors.ballotElectionCard : primitiveColors.blue1;
  // Match the canvas to the strip so Android's edge-to-edge system-bar region
  // (below this fixed strip) reads the same color.
  useCanvasBackground(fillColor);
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: { xs: 'block', md: 'none' },
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        // Extend 1px above the safe-area top so the filler's pure
        // color paints over the bottom-most pixel of whatever sits
        // above it (BallotSheet header at closed, sheet body at open,
        // chat container otherwise). Without the overlap, subpixel
        // rendering at the boundary could blend the element above with
        // its own underlying bg, producing a hairline that reads as
        // a different shade (or "applied opacity") next to the filler.
        height: 'calc(env(safe-area-inset-bottom) + 1px)',
        backgroundColor: fillColor,
        // Above the chat (default z), the backdrop (modal), AND the
        // BallotSheet (modal + 1). Lifting above the sheet lets the
        // 1px overlap region paint the filler's solid color over the
        // sheet's bottom-most pixel, which guarantees a clean color
        // match with the sheet header / body / chat above.
        zIndex: (theme) => theme.zIndex.modal + 2,
        pointerEvents: 'none',
      }}
    />
  );
};

export default SafeAreaFiller;
